import { CYCLE_AUDIENCE_FILTER_LAYOUT } from '@amelio/ui-library';

/**
 * Sizing, typography and surface classes shared by the compensation report. Sizes are written out
 * where the design lands between two steps of the scale, and named here so the three report pages
 * cannot drift apart.
 *
 * A bare utility the library's compiled sheet also emits (`flex-col`, `gap-6`, `grid-cols-2`…)
 * beats any `bp:` override of the same property, so a responsive layout is written either as a
 * `max-*` / `bp:` pair of mutually exclusive variants, or as a grid whose base `grid-cols-1` the
 * library never emits.
 */

/** Numbered section heading ("1. Audience de l'enveloppe"). */
export const COMPENSATION_SECTION_TITLE = 'text-[13px] font-bold tracking-wide uppercase';

/** Overline above a figure ("Réparti", "Masse salariale"). */
export const COMPENSATION_OVERLINE = 'text-[11px] font-semibold tracking-[0.12em] uppercase';

/** Caption inside a tile (headcount, help text, share of salary). */
export const COMPENSATION_CAPTION = 'text-[12px]';

/** Headline figure inside a tile (payroll, allocated, threshold). */
export const COMPENSATION_TILE_VALUE = 'text-[18px] font-bold';

/** Inner tile surface, on an opaque token so no opacity variant can be clobbered. */
export const COMPENSATION_TILE_SURFACE = 'bg-base-25 rounded-[12px] p-[14px]';

/**
 * The adjustments table is a CSS grid carrying table roles, as in the competency matrix. Each track
 * has a minimum wide enough for its control; the performance track holds the longest default level
 * name on one line, so no row grows taller than its neighbours.
 */
export const COMPENSATION_TABLE_GRID =
    'grid grid-cols-[minmax(180px,18fr)_minmax(225px,22fr)_minmax(115px,11fr)_minmax(70px,8fr)_minmax(165px,17fr)_minmax(116px,12fr)_minmax(110px,12fr)] items-center gap-x-[16px]';

/** A body row: one separator across the whole row (per cell, the column gaps cut it into dashes). */
export const COMPENSATION_TABLE_ROW = `${COMPENSATION_TABLE_GRID} border-base-100 hover:bg-base-25 border-b transition-colors`;

/** Column header. Allowed to wrap: held on one line, a long label squeezes its sort caret away. */
export const COMPENSATION_TABLE_HEADER = 'text-[11px] font-semibold tracking-[0.12em] uppercase';

/** Title and controls side by side from `md`; stacked below, the controls keep their natural width. */
export const COMPENSATION_SPLIT_ROW =
    'flex gap-[16px] max-md:flex-col max-md:items-start md:items-center md:justify-between';

/** Holds the place of one criterion card while the company's attributes load, so the counters below
 *  do not jump when the cards arrive. */
export const COMPENSATION_AUDIENCE_CRITERION_PLACEHOLDER = `h-[62px] w-full ${CYCLE_AUDIENCE_FILTER_LAYOUT.rowRadius}`;

/** Budget / strategy split of section 2: stacked below `lg`, one third and two thirds from `lg`. */
export const COMPENSATION_BUDGET_LAYOUT =
    'grid grid-cols-1 gap-[24px] lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]';

/** As many level tiles abreast as fit in 200px each, sized against the column rather than the
 *  viewport, since the tiles live in the two-thirds column of section 2. */
export const COMPENSATION_LEVEL_TILES_LAYOUT =
    'grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[12px]';

export const COMPENSATION_HISTORY_LIST =
    'scrollbar-glass flex max-h-[60vh] flex-col gap-[12px] overflow-y-auto';

/** Above the sections scrolling beneath it, below any dialog (`Modal` uses z-50). */
export const COMPENSATION_ACTION_BAR_Z_INDEX = 40;

/** A tab's trailing budget, one step under the tab label. */
export const COMPENSATION_TAB_BUDGET = 'text-[11px] font-medium';
